import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { API_URL } from '../../api/config';

const BillDetails = () => {
  const { billId } = useParams();
  const { userToken } = useAuth();
  const [bill, setBill] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBillDetails();
  }, [billId]);

  const fetchBillDetails = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/bills/${billId}`, {
        headers: {
          Authorization: `Bearer ${userToken}`,
        },
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.message);

      setBill(data);
    } catch (error) {
      alert(error.message);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'paid':
        return 'bg-emerald-500 text-white';
      case 'pending':
        return 'bg-amber-400 text-black';
      case 'unpaid':
        return 'bg-red-500 text-white';
      default:
        return 'bg-gray-500 text-white';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!bill) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-50">
        <div className="text-xl text-gray-600">Bill not found</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-8 pb-20">
      <div className="max-w-3xl mx-auto px-4">
        <div className="bg-white rounded-lg shadow-md overflow-hidden mb-6">
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-4">
            <div className="flex justify-between items-center">
              <h1 className="text-2xl font-bold text-white">Bill #{bill.billNumber}</h1>
              <span className={`px-3 py-1 rounded-full text-sm font-semibold capitalize ${getStatusColor(bill.status)}`}>
                {bill.status}
              </span>
            </div>
          </div>

          <div className="p-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Bill Information</h2>
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="bg-gray-50 p-3 rounded-md">
                <p className="text-sm text-gray-500">Date</p>
                <p className="font-medium text-gray-800">{new Date(bill.createdAt).toLocaleString()}</p>
              </div>
              <div className="bg-gray-50 p-3 rounded-md">
                <p className="text-sm text-gray-500">Table</p>
                <p className="font-medium text-gray-800">{bill.tableNumber}</p>
              </div>
              <div className="bg-gray-50 p-3 rounded-md">
                <p className="text-sm text-gray-500">Payment Status</p>
                <p className="font-medium text-gray-800">{bill.paymentStatus}</p>
              </div>
              <div className="bg-gray-50 p-3 rounded-md">
                <p className="text-sm text-gray-500">Payment Method</p>
                <p className="font-medium text-gray-800">{bill.paymentMethod || 'Not specified'}</p>
              </div>
            </div>

            <h2 className="text-lg font-semibold text-gray-800 mb-4 mt-8">Items</h2>
            <div className="border rounded-lg overflow-hidden mb-8">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Item</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Quantity</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Price</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {bill.items.map((item, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="font-medium text-gray-900">{item.itemName}</div>
                        {item.variant && (
                          <div className="text-sm text-gray-500">({item.variant.name})</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        x{item.quantity}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                        ₹{(item.price * item.quantity).toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <h2 className="text-lg font-semibold text-gray-800 mb-4">Payment Details</h2>
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <div className="flex justify-between py-2">
                <span className="text-gray-600">Subtotal</span>
                <span className="font-medium">₹{bill.subtotal.toFixed(2)}</span>
              </div>

              {(bill.cgst > 0 || bill.sgst > 0) && (
                <>
                  <div className="flex justify-between py-2 border-t border-gray-200">
                    <span className="text-gray-600">CGST</span>
                    <span className="font-medium">₹{bill.cgst.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between py-2 border-t border-gray-200">
                    <span className="text-gray-600">SGST</span>
                    <span className="font-medium">₹{bill.sgst.toFixed(2)}</span>
                  </div>
                </>
              )}

              {(bill.discountPercentage > 0 || bill.discountAmount > 0) && (
                <div className="flex justify-between py-2 border-t border-gray-200">
                  <span className="text-gray-600">
                    Discount {bill.discountPercentage > 0 ? `(${bill.discountPercentage}%)` : ''}
                  </span>
                  <span className="font-medium text-red-500">-₹{bill.discountAmount.toFixed(2)}</span>
                </div>
              )}

              <div className="flex justify-between py-3 border-t border-gray-300 mt-2">
                <span className="text-lg font-bold text-gray-800">Total</span>
                <span className="text-lg font-bold text-emerald-600">₹{bill.totalAmount.toFixed(2)}</span>
              </div>
            </div>

            {bill.names && (
              <div className="mt-6">
                <h2 className="text-lg font-semibold text-gray-800 mb-2">Names</h2>
                <p className="text-gray-600 italic">{bill.names}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BillDetails;